/**
 * Everything the platform-administration screens read and write.
 *
 * Two of these calls deliberately read across brands, and both are admin-only
 * on the server: the master brand list and the cross-brand performance board.
 * They are the only reads in the console whose answer is not the active brand,
 * which is why the screens that use them carry the striped banner.
 *
 * The rest are ordinary writes about people: invite, move between administrator
 * and member, suspend, and brand membership.
 */
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { UseInfiniteQueryResult, UseQueryResult } from '@tanstack/react-query'
import { api } from '../../lib/api'
import { qk } from '../../lib/queryClient'
import type { EmailProvider, IsoDateTime, Paginated, Uuid } from '../../lib/types'
import type {
  GlobalRecipientAnswer,
  GlobalRecipientBody,
  GlobalRecipientStopAnswer,
  GlobalTestRecipient,
} from './global-test-recipient'
import type { InvitationEmailOutcome } from './invite-outcome'

// ---------------------------------------------------------------------------
// The master view — GET /brands/master
// ---------------------------------------------------------------------------

export interface MasterBrandRow {
  id: Uuid
  name: string
  slug: string
  status: string
  tz: string
  memberCount: number
  knowledgeValid: boolean
  knowledgeVersion: number | null
  /** Active, with a valid brand file. The only brands that can start a campaign. */
  canLaunch: boolean
  createdAt: IsoDateTime
}

export interface MasterBrands extends Paginated<MasterBrandRow> {
  scope: 'all-brands'
}

export function useMasterBrands(): UseQueryResult<MasterBrands> {
  return useQuery({
    queryKey: qk.masterBrands(),
    queryFn: () => api.get<MasterBrands>('/brands/master'),
  })
}

// ---------------------------------------------------------------------------
// Cross-brand performance — GET /analytics/cross-brand
// ---------------------------------------------------------------------------

export interface CrossBrandBrand {
  brandId: Uuid
  brandName: string
  sent: number
  contacted: number
  replies: number
  positives: number
  meetings: number
  replyRate: number | null
  positiveRate: number | null
  campaignCount: number
}

export interface CrossBrandCampaign extends Omit<CrossBrandBrand, 'campaignCount'> {
  campaignId: Uuid | null
  campaignName: string
  campaignStatus: string | null
  /** False when too few people were contacted for the rates to mean anything. */
  comparable: boolean
}

export interface CrossBrandReport {
  scope: 'all-brands'
  from: string
  to: string
  generatedAt: IsoDateTime
  minContactedForRanking: number
  totals: {
    sent: number
    contacted: number
    replies: number
    positives: number
    meetings: number
    replyRate: number | null
  }
  brands: CrossBrandBrand[]
  campaigns: CrossBrandCampaign[]
}

export function useCrossBrand(days: number): UseQueryResult<CrossBrandReport> {
  return useQuery({
    queryKey: qk.analyticsCrossBrand({ days }),
    queryFn: () => api.get<CrossBrandReport>('/analytics/cross-brand', { params: { days } }),
  })
}

// ---------------------------------------------------------------------------
// People — GET /users and its writes
// ---------------------------------------------------------------------------

export type PlatformRole = 'admin' | 'member'
export type MembershipRole = 'owner' | 'admin' | 'member'
export type AccountStatus = 'active' | 'suspended'

export interface PersonBrand {
  brandId: Uuid
  name: string
  role: MembershipRole
}

export interface Person {
  userId: Uuid
  name: string
  email: string
  /** Their picture. Null draws initials, which is what most people will have. */
  avatarUrl?: string | null
  role: PlatformRole
  status: AccountStatus
  createdAt: IsoDateTime
  lastLoginAt: IsoDateTime | null
  brands: PersonBrand[]
  /**
   * Set when they are a test recipient in every brand, with the addresses
   * vouched for them there. Null when they are not, and absent from an older
   * server, which reads the same.
   */
  globalTestRecipient?: GlobalTestRecipient | null
}

export function usePeople(enabled = true): UseQueryResult<Paginated<Person>> {
  return useQuery({
    queryKey: qk.users(),
    queryFn: () => api.get<Paginated<Person>>('/users'),
    enabled,
  })
}

export interface InviteInput {
  email: string
  name: string
  role: PlatformRole
  brandId?: Uuid
  brandRole?: MembershipRole
}

export interface InviteResult extends Person {
  /** Present only when the server made the password up. Shown once, never stored. */
  temporaryPassword: string | null
  /**
   * Whether the sign-in details were also emailed to the new person.
   *
   * The email is in addition to the password above, never instead of it, and it
   * is allowed to fail without failing the invitation. By the time the send is
   * attempted the account exists, so a mail outage that answered with an error
   * would leave the admin pressing the button again and being told the address
   * is taken. This block is how the screen knows whether to say the password
   * was delivered or that it is now the only copy.
   */
  invitationEmail: InvitationEmailOutcome
}

function useAfterPeopleChange(): () => void {
  const client = useQueryClient()
  return () => {
    void client.invalidateQueries({ queryKey: qk.users() })
    void client.invalidateQueries({ queryKey: qk.brands() })
    void client.invalidateQueries({ queryKey: qk.session() })
  }
}

export function useInvitePerson() {
  const after = useAfterPeopleChange()
  return useMutation({
    mutationFn: (body: InviteInput) => api.post<InviteResult>('/users', body),
    onSuccess: after,
  })
}

export function useSetPlatformRole() {
  const after = useAfterPeopleChange()
  return useMutation({
    mutationFn: (input: { userId: Uuid; role: PlatformRole }) =>
      api.post<{ userId: Uuid; role: PlatformRole; changed: boolean }>(`/users/${input.userId}/role`, {
        role: input.role,
      }),
    onSuccess: after,
  })
}

export function useSetAccountStatus() {
  const after = useAfterPeopleChange()
  return useMutation({
    mutationFn: (input: { userId: Uuid; status: AccountStatus }) =>
      api.post<{ userId: Uuid; status: AccountStatus; changed: boolean }>(`/users/${input.userId}/status`, {
        status: input.status,
      }),
    onSuccess: after,
  })
}

// ---------------------------------------------------------------------------
// A test recipient in every brand: POST and DELETE /users/:id/global-test-recipient
// ---------------------------------------------------------------------------

/**
 * Both writes fan out to every brand, so what they change is the Team section
 * of whichever brand is open as well as this list. The team lists and the
 * campaign pickers are refreshed along with the people.
 */
function useAfterGlobalRecipientChange(): () => void {
  const after = useAfterPeopleChange()
  const client = useQueryClient()
  return () => {
    after()
    void client.invalidateQueries({ queryKey: ['prospects'] })
    void client.invalidateQueries({ queryKey: ['playbooks'] })
    void client.invalidateQueries({ queryKey: ['campaigns'] })
  }
}

/** Make somebody a test recipient in every brand, or save their addresses again. Repeatable. */
export function useSetGlobalTestRecipient() {
  const after = useAfterGlobalRecipientChange()
  return useMutation({
    mutationFn: (input: { userId: Uuid; body: GlobalRecipientBody }) =>
      api.post<GlobalRecipientAnswer>(`/users/${input.userId}/global-test-recipient`, input.body),
    onSuccess: after,
  })
}

/** Stop, in every brand at once. */
export function useStopGlobalTestRecipient() {
  const after = useAfterGlobalRecipientChange()
  return useMutation({
    mutationFn: (userId: Uuid) => api.del<GlobalRecipientStopAnswer>(`/users/${userId}/global-test-recipient`),
    onSuccess: after,
  })
}

// ---------------------------------------------------------------------------
// Registering a sending account — POST /channel-accounts
// ---------------------------------------------------------------------------

export interface CreateChannelAccountInput {
  channel: string
  handle: string
  label?: string
  /** Absent means you. Naming somebody else needs an admin. */
  ownerUserId?: Uuid
  tz?: string
  /** Email only. Absent means Instantly, which is what every mailbox here was. */
  provider?: EmailProvider
  /**
   * The app password, for a mailbox that sends as itself. Sent once, on the way
   * in, and never sent back: the server encrypts it and no endpoint returns it.
   */
  password?: string
}

/**
 * Replace the password on a mailbox that sends as itself.
 *
 * Write-only by design, which is why there is no matching read hook anywhere in
 * this file: the value cannot be fetched back, so a screen can offer to change
 * it and can never show it.
 */
export function useSetAccountPassword() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { id: Uuid; password: string }) =>
      api.put<{ id: Uuid }>(`/channel-accounts/${input.id}/password`, { password: input.password }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() })
    },
  })
}

export function useCreateChannelAccount() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: CreateChannelAccountInput) => api.post<{ id: Uuid; ownerUserId: Uuid }>('/channel-accounts', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() })
      void client.invalidateQueries({ queryKey: qk.analyticsAccounts() })
    },
  })
}

/** One mailbox the Instantly workspace holds, as the import describes it. */
export interface InstantlyCandidate {
  address: string
  name: string | null
  dailyLimit: number | null
  warmupActive: boolean
  setupPending: boolean
  ownerUserId: Uuid
}

export interface InstantlyImportReport {
  applied: boolean
  found: number
  alreadyHere: string[]
  /** Addresses refused at dispatch, so importing one makes an account that can never send. */
  unsendable: { address: string; why: string }[]
  /** Email accounts this platform holds that the workspace has never heard of. */
  notInInstantly: string[]
  wouldCreate: InstantlyCandidate[]
  created: { id: Uuid; address: string; ownerUserId: Uuid }[]
}

/**
 * Read the workspace, or import from it.
 *
 * The same call does both, and `apply` is the difference. Without it nothing is
 * written, which is what makes it safe to press first: a sending account
 * carries an owner, the owner's number is the allowance the account starts
 * from, and only a person can say whose mailbox is whose.
 */
export function useInstantlyImport() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (body: { apply?: boolean; defaultOwnerUserId?: Uuid; owners?: Record<string, Uuid> }) =>
      api.post<InstantlyImportReport>('/channel-accounts/import/instantly', body),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() })
      void client.invalidateQueries({ queryKey: qk.analyticsAccounts() })
    },
  })
}

/** Move a sending account to a different person. Admin only. */
export function useChangeAccountOwner() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { accountId: Uuid; ownerUserId: Uuid; reason?: string }) =>
      api.post<{ id: Uuid; ownerUserId: Uuid; changed: boolean }>(`/channel-accounts/${input.accountId}/owner`, {
        ownerUserId: input.ownerUserId,
        ...(input.reason === undefined ? {} : { reason: input.reason }),
      }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() })
    },
  })
}

// ---------------------------------------------------------------------------
// "Same account?" (docs/ACCOUNT-POLICY.md rule 7) — GET /channel-accounts/merge-suggestions
// ---------------------------------------------------------------------------

/** A newer account found where an older one on the same app used to be, while the older one is on no phone. */
export interface MergeSuggestion {
  channel: string
  newAccountId: Uuid
  newHandle: string
  newDisplayName: string | null
  oldAccountId: Uuid
  oldHandle: string
  oldDisplayName: string | null
  deviceId: string
  /** The phone by its name, which is what the banner says. */
  phoneName: string
  androidUser: number
  oldGoneAt: IsoDateTime | null
}

const mergeSuggestionsKey = ['channel-accounts', 'merge-suggestions'] as const

export function useMergeSuggestions(enabled: boolean) {
  return useQuery({
    queryKey: mergeSuggestionsKey,
    queryFn: () => api.get<{ items: MergeSuggestion[] }>('/channel-accounts/merge-suggestions'),
    enabled,
  })
}

export function useMergeAccount() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (pair: Pick<MergeSuggestion, 'newAccountId' | 'oldAccountId'>) =>
      api.post<{ handle: string; retiredHandle: string; leftBehind: string[] }>(
        `/channel-accounts/${pair.newAccountId}/merge-into/${pair.oldAccountId}`,
        {},
      ),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.channelAccounts() })
    },
  })
}

export function useDismissMergeSuggestion() {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (pair: Pick<MergeSuggestion, 'newAccountId' | 'oldAccountId'>) =>
      api.post<{ dismissed: boolean }>(`/channel-accounts/${pair.newAccountId}/merge-suggestions/${pair.oldAccountId}/dismiss`, {}),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: mergeSuggestionsKey })
    },
  })
}

// ---------------------------------------------------------------------------
// Creating a brand and changing how it looks — POST /brands, PATCH /brands/:id
// ---------------------------------------------------------------------------

export interface CreateBrandInput {
  name: string
  slug?: string
  tz?: string
  logoSvg?: string | null
}

export interface BrandUpdateInput {
  brandId: Uuid
  name?: string
  tz?: string
  status?: 'active' | 'paused' | 'archived'
  logoSvg?: string | null
  defaultLanguage?: string | null
  greetingStyle?: 'plain' | 'salaam' | null
}

/** What the sanitiser stripped, so a mangled mark is a sentence and not a mystery. */
export interface BrandWriteResult {
  id: Uuid
  name: string
  slug: string
  logoSvg: string | null
  removedFromMark?: string[]
}

export function useCreateBrand() {
  const after = useAfterPeopleChange()
  return useMutation({
    mutationFn: (body: CreateBrandInput) => api.post<BrandWriteResult>('/brands', body),
    onSuccess: after,
  })
}

export function useUpdateBrand() {
  const after = useAfterPeopleChange()
  return useMutation({
    mutationFn: ({ brandId, ...body }: BrandUpdateInput) =>
      api.patch<BrandWriteResult>(`/brands/${brandId}`, body),
    onSuccess: after,
  })
}

// ---------------------------------------------------------------------------
// Brand membership — POST and DELETE /brands/:id/members
// ---------------------------------------------------------------------------

export function useAddBrandMember() {
  const after = useAfterPeopleChange()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { brandId: Uuid; userId: Uuid; role: MembershipRole }) =>
      api.post<{ brandId: Uuid; userId: Uuid; role: MembershipRole; added: boolean }>(
        `/brands/${input.brandId}/members`,
        { userId: input.userId, role: input.role },
      ),
    onSuccess: (_data, variables) => {
      after()
      void client.invalidateQueries({ queryKey: [...qk.brand(variables.brandId), 'members'] })
    },
  })
}

export function useRemoveBrandMember() {
  const after = useAfterPeopleChange()
  const client = useQueryClient()
  return useMutation({
    mutationFn: (input: { brandId: Uuid; userId: Uuid }) =>
      api.del<{ removed: boolean }>(`/brands/${input.brandId}/members/${input.userId}`),
    onSuccess: (_data, variables) => {
      after()
      void client.invalidateQueries({ queryKey: [...qk.brand(variables.brandId), 'members'] })
    },
  })
}

// ---------------------------------------------------------------------------
// The brand booking link — GET and PUT /brands/:id/meeting-link
// ---------------------------------------------------------------------------

export interface MeetingLink {
  brandId: Uuid
  link: string | null
  updatedAt: IsoDateTime | null
}

export function useMeetingLink(brandId: Uuid | null | undefined): UseQueryResult<MeetingLink> {
  return useQuery({
    queryKey: qk.meetingLink(brandId ?? 'none'),
    queryFn: () => api.get<MeetingLink>(`/brands/${brandId ?? ''}/meeting-link`),
    enabled: brandId !== null && brandId !== undefined,
    staleTime: 5 * 60_000,
  })
}

export function useSaveMeetingLink(brandId: Uuid | null | undefined) {
  const client = useQueryClient()
  return useMutation({
    mutationFn: (link: string | null) =>
      api.put<MeetingLink>(`/brands/${brandId ?? ''}/meeting-link`, { link }),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: qk.meetingLink(brandId ?? 'none') })
    },
  })
}

/**
 * The sentence the platform appends when it adds the booking link to a drafted
 * reply. Mirrors `insertCalendarLink` in the inbox module, so a reply a person
 * writes by hand reads exactly like one the platform completed.
 */
export function meetingLinkSentence(link: string): string {
  return `If it is easier, you can pick a time here: ${link}`
}

// ---------------------------------------------------------------------------
// Everything, everywhere — GET /campaigns/master and GET /prospects/master
// ---------------------------------------------------------------------------

/**
 * The two admin-wide lists.
 *
 * `admin` already reaches every brand, but the ordinary campaign and prospect
 * screens are scoped to whichever brand the top bar is on, so "show me all of
 * it" meant switching brand by brand and holding the total in your head. These
 * two do not move when the brand switcher does, which is why the screens that
 * use them wear the same striped banner as the master view.
 */
export interface AdminCampaignRow {
  id: Uuid
  brandId: Uuid
  brandName: string
  name: string
  status: string
  methodName: string
  /** Whoever created it, by user id. Absent from an older server. */
  createdBy?: Uuid | null
  createdByName: string | null
  /** Prospects in it. Teammates marked as test recipients are in `teamTests`. */
  enrolled: number
  /** Messages to prospects. */
  sentTotal: number
  replies: number
  /** Teammates in it and messages to them. Absent from an older server. */
  teamTests?: { people: number; sent: number }
  startedAt: IsoDateTime | null
  createdAt: IsoDateTime
}

export interface AdminProspectRow {
  id: Uuid
  brandId: Uuid
  brandName: string
  fullName: string | null
  title: string | null
  company: string | null
  country: string | null
  status: string
  createdAt: IsoDateTime
}

export interface AdminListFilters {
  brandId?: string
  q?: string
  status?: string
  /** A user id. Only the campaign list reads it: a prospect has no creator. */
  createdBy?: string
}

export function queryString(filters: AdminListFilters, cursor?: string | null): string {
  const params = new URLSearchParams()
  if (filters.brandId) params.set('brandId', filters.brandId)
  if (filters.q) params.set('q', filters.q)
  if (filters.status) params.set('status', filters.status)
  if (filters.createdBy) params.set('createdBy', filters.createdBy)
  if (cursor) params.set('cursor', cursor)
  const rendered = params.toString()
  return rendered === '' ? '' : `?${rendered}`
}

/**
 * Paged, not capped.
 *
 * These two answer "show me everything", and a table that quietly stops at the
 * first fifty while the count above it says two hundred is the exact shape of
 * answer that gets trusted and is wrong. They page the way the prospects list
 * does, and the screens say how many of the total are on screen.
 */
export function useAdminCampaigns(
  filters: AdminListFilters = {},
): UseInfiniteQueryResult<{ pages: Paginated<AdminCampaignRow>[] }> {
  return useInfiniteQuery({
    queryKey: qk.masterCampaigns(filters as Record<string, string | undefined>),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api.get<Paginated<AdminCampaignRow>>(`/campaigns/master${queryString(filters, pageParam)}`, { signal }),
    getNextPageParam: (last) => last.cursor ?? undefined,
  }) as UseInfiniteQueryResult<{ pages: Paginated<AdminCampaignRow>[] }>
}

export function useAdminProspects(
  filters: AdminListFilters = {},
): UseInfiniteQueryResult<{ pages: Paginated<AdminProspectRow>[] }> {
  return useInfiniteQuery({
    queryKey: qk.masterProspects(filters as Record<string, string | undefined>),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam, signal }) =>
      api.get<Paginated<AdminProspectRow>>(`/prospects/master${queryString(filters, pageParam)}`, { signal }),
    getNextPageParam: (last) => last.cursor ?? undefined,
  }) as UseInfiniteQueryResult<{ pages: Paginated<AdminProspectRow>[] }>
}
